import { Briefcase as PageHeadingIcon } from '@phosphor-icons/react';
import StartupPageTitle from '../components/StartupPageTitle';
/**
 * EngagementWorkspace.jsx — unified founder ↔ agency engagement view.
 *
 * Sprint 2/3/4 — Modules 02 (Brief + SOW + Contracts + Payments)
 *                       05 (Request pipeline + revisions)
 *                       07 (Visual content calendar)
 *
 * Routes:
 *   /startup/studio/engagements/:engagementId   (founder)
 *   /agency/engagements/:engagementId           (agency teammate)
 *
 * One component, two sides — side is decided by the backend response.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/api';
import { useCurrency } from '@/hooks/useCurrency';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, Notepad, Kanban, CalendarBlank, Receipt, ShieldCheck,
  Plus, X, Check, Clock, Warning, CaretDown, CaretLeft, CaretRight, ArrowRight,
  Trash, ChatCircle, PaperPlaneTilt, ArrowsClockwise, UploadSimple, Brain, ChartLineUp,
  ThumbsUp, ThumbsDown,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REQUEST_STATUSES = [
  { id: 'new',         label: 'New',           color: '#64748b' },
  { id: 'in_progress', label: 'In progress',   color: '#6366f1' },
  { id: 'review',      label: 'In review',     color: '#f59e0b' },
  { id: 'revisions',   label: 'Revisions',     color: '#ef4444' },
  { id: 'approved',    label: 'Approved',      color: '#10b981' },
];

const PLATFORMS = [
  'linkedin_post', 'linkedin_article', 'twitter_post', 'instagram_post',
  'instagram_reel', 'instagram_story', 'short_form_video', 'youtube_video',
  'blog', 'newsletter', 'press_release', 'website', 'deck', 'other',
];

const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const todayKey = () => new Date().toISOString().slice(0, 10);

const EngagementWorkspace = ({ user }) => {
  const { engagementId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  const refresh = async () => {
    try {
      const r = await api.get(`/engagements/${engagementId}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load engagement');
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await api.get(`/engagements/${engagementId}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load engagement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [engagementId]);

  const side = data?.side;
  const accent = side === 'agency' ? '#6366f1' : '#10b981';

  if (loading) return <div className="p-12 text-sm text-text-muted flex items-center gap-2"><ArrowsClockwise size={14} className="animate-spin" /> Loading…</div>;
  if (!data) return <div className="p-12 text-sm text-text-muted">Engagement not found.</div>;

  const { engagement, brief, sow, milestones, payments, contracts, requests_total } = data;
  const TABS = [
    { id: 'overview',  label: 'Overview',  icon: FileText },
    { id: 'brief',     label: 'Brief',     icon: Notepad },
    { id: 'sow',       label: 'SOW',       icon: FileText },
    { id: 'pipeline',  label: 'Pipeline',  icon: Kanban,   badge: requests_total },
    { id: 'calendar',  label: 'Calendar',  icon: CalendarBlank },
    { id: 'brand',     label: 'Brand',     icon: Brain },
    { id: 'metrics',   label: 'Metrics',   icon: ChartLineUp },
    { id: 'payments',  label: 'Payments',  icon: Receipt },
    { id: 'contracts', label: 'Contracts', icon: ShieldCheck },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1280px] mx-auto pb-24" data-testid="engagement-workspace">
      <button onClick={() => navigate(side === 'agency' ? '/agency' : '/startup/marketing')} className="text-xs text-text-muted hover:text-text-primary inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={11} weight="bold" /> Back
      </button>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6 startup-page-header">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: accent }}>
            Engagement · {engagement.status.toUpperCase()}
          </div>
          <StartupPageTitle icon={PageHeadingIcon} className="text-3xl sm:text-4xl font-heading font-bold text-text-primary leading-[1.05] mb-2" style={{ letterSpacing: '-0.02em' }}>
            {side === 'agency' ? engagement.founder_startup_name || engagement.founder_full_name : engagement.agency_name}
          </StartupPageTitle>
          <p className="text-sm text-text-secondary">
            {side === 'agency' ? `With founder ${engagement.founder_full_name}` : `With agency ${engagement.agency_name}`}
            {' · '}
            <span className="font-mono">Started {fmtDate(engagement.created_at)}</span>
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <StatusPill label="Brief"     value={engagement.brief_status} testid="eng-status-brief" />
          <StatusPill label="SOW"       value={engagement.sow_status} testid="eng-status-sow" />
          <StatusPill label="Status"    value={engagement.status} testid="eng-status-overall" highlight />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border mb-6 overflow-x-auto" data-testid="engagement-tabs">
        <div className="flex items-center gap-1 min-w-max">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`eng-tab-${t.id}`}
                className={`relative px-4 py-2.5 text-sm font-medium inline-flex items-center gap-1.5 ${active ? 'text-text-primary' : 'text-text-muted hover:text-text-secondary'}`}
              >
                <Icon size={13} weight="bold" /> {t.label}
                {t.badge ? <span className="ml-1 text-[10px] font-mono bg-surface px-1.5 py-0.5 rounded">{t.badge}</span> : null}
                {active && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: accent }} />}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'overview'  && <OverviewTab data={data} accent={accent} setTab={setTab} />}
      {tab === 'brief'     && <BriefTab    data={data} accent={accent} refresh={refresh} />}
      {tab === 'sow'       && <SOWTab      data={data} accent={accent} refresh={refresh} />}
      {tab === 'pipeline'  && <PipelineTab engagementId={engagementId} side={side} accent={accent} milestones={milestones} engagementStatus={engagement.status} />}
      {tab === 'calendar'  && <CalendarTab engagementId={engagementId} side={side} accent={accent} />}
      {tab === 'brand'     && <BrandTab    engagementId={engagementId} side={side} accent={accent} />}
      {tab === 'metrics'   && <MetricsTab  engagementId={engagementId} side={side} accent={accent} />}
      {tab === 'payments'  && <PaymentsTab data={data} accent={accent} refresh={refresh} />}
      {tab === 'contracts' && <ContractsTab data={data} accent={accent} refresh={refresh} />}
    </div>
  );
};

/* ---------- shared bits ---------- */
const StatusPill = ({ label, value, testid, highlight }) => {
  const v = (value || 'not_started').replace(/_/g, ' ');
  const dotColors = { not_started: '#64748b', in_progress: '#6366f1', drafted: '#f59e0b', submitted: '#10b981', accepted: '#10b981', active: '#10b981', draft: '#64748b', negotiating: '#f59e0b', completed: '#10b981', paused: '#f59e0b', declined: '#ef4444' };
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1.5 px-2.5 py-1 rounded ${highlight ? 'bg-surface' : ''}`} data-testid={testid}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dotColors[value] || '#64748b' }} />
      <span className="text-text-muted">{label}:</span> <span className="text-text-primary">{v}</span>
    </span>
  );
};

/* ---------- OVERVIEW ---------- */
const OverviewTab = ({ data, accent, setTab }) => {
  const { format } = useCurrency();
  const { engagement, brief, sow, milestones, payments, contracts } = data;
  const totalPaid = (payments || []).filter((p) => p.status === 'paid').reduce((s, p) => s + (p.amount || 0), 0);
  const totalDue = (payments || []).reduce((s, p) => s + (p.amount || 0), 0);
  const payCur = (payments && payments[0]?.currency) || 'INR';
  const done = (milestones || []).filter((m) => m.status === 'done').length;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="engagement-overview">
      <Card onClick={() => setTab('brief')} accent={accent} title="Brief" status={engagement.brief_status}>
        {brief ? (
          <p className="text-xs text-text-secondary line-clamp-3">{brief.objectives || 'No objectives yet.'}</p>
        ) : <p className="text-xs text-text-muted">Not yet started.</p>}
      </Card>
      <Card onClick={() => setTab('sow')} accent={accent} title="Statement of Work" status={engagement.sow_status}>
        {sow ? (
          <div className="text-xs text-text-secondary">
            <div className="font-mono font-semibold text-text-primary text-lg">{format(sow.total_amount || 0, sow.currency || 'INR')}</div>
            <div>{(sow.items || []).length} line item{(sow.items || []).length === 1 ? '' : 's'} · {(sow.payment_schedule || []).length} payment{(sow.payment_schedule || []).length === 1 ? '' : 's'}</div>
          </div>
        ) : <p className="text-xs text-text-muted">Awaiting agency draft.</p>}
      </Card>
      <Card onClick={() => setTab('payments')} accent={accent} title="Payments">
        <div className="text-xs text-text-secondary">
          <div className="font-mono font-semibold text-text-primary text-lg">{format(totalPaid, payCur)} <span className="text-xs text-text-muted">/ {format(totalDue, payCur)}</span></div>
          <div>{payments.length} schedule entr{payments.length === 1 ? 'y' : 'ies'}</div>
        </div>
      </Card>
      <Card onClick={() => setTab('pipeline')} accent={accent} title="Pipeline">
        <div className="text-xs text-text-secondary">
          <div className="font-mono font-semibold text-text-primary text-lg">{done} / {milestones.length}</div>
          <div>milestones delivered</div>
        </div>
      </Card>
      <Card onClick={() => setTab('contracts')} accent={accent} title="Contracts / Vault">
        <div className="text-xs text-text-secondary">
          <div className="font-mono font-semibold text-text-primary text-lg">{contracts.length}</div>
          <div>document{contracts.length === 1 ? '' : 's'}</div>
        </div>
      </Card>
    </div>
  );
};

const Card = ({ onClick, accent, title, status, children }) => (
  <button onClick={onClick} className="text-left bg-surface border border-border rounded-xl p-5 hover:border-text-primary transition-colors">
    <div className="flex items-center justify-between mb-3">
      <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-text-muted">{title}</div>
      {status && (
        <span className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider" style={{ backgroundColor: `${accent}1a`, color: accent }}>{status.replace(/_/g, ' ')}</span>
      )}
    </div>
    {children}
  </button>
);

/* ---------- BRIEF ---------- */
const BRIEF_FIELDS = [
  ['objectives',       'Objectives',          'What outcome do you want this engagement to drive?',                 4],
  ['target_audience',  'Target audience',     'Who are we writing/designing for?',                                   3],
  ['success_metrics',  'Success metrics',     'What does success look like in 90 days? (numbers welcome)',           3],
  ['brand_voice',      'Brand voice',         'Tone of voice, must-says, must-not-says.',                            3],
  ['references',       'References & inspiration', 'Links, screenshots, accounts you love.',                         3],
  ['timeline',         'Timeline expectations', 'When do you need this live?',                                       2],
  ['budget_range',     'Budget range',        'Ballpark — gives the agency context for the SOW.',                    2],
  ['constraints',      'Constraints',         'Anything off-limits? Compliance? Existing tools?',                    3],
];

const BriefTab = ({ data, accent, refresh }) => {
  const { brief, side, engagement } = { ...data, side: data.side };
  const readOnly = side === 'agency';
  const [form, setForm] = useState({
    objectives: brief?.objectives || '',
    target_audience: brief?.target_audience || '',
    success_metrics: brief?.success_metrics || '',
    brand_voice: brief?.brand_voice || '',
    references: brief?.references || '',
    timeline: brief?.timeline || '',
    budget_range: brief?.budget_range || '',
    constraints: brief?.constraints || '',
  });
  const [busy, setBusy] = useState(false);
  const save = async (submit = false) => {
    setBusy(true);
    try {
      await api.patch(`/engagements/${engagement.id}/brief`, { ...form, submit });
      toast.success(submit ? 'Brief submitted to the agency' : 'Saved');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl space-y-5" data-testid="engagement-brief">
      {readOnly && (
        <div className="bg-surface border-l-2 rounded-r-lg p-3 text-xs text-text-secondary" style={{ borderLeftColor: accent }}>
          Read-only — the founder owns the brief. Use it to shape the SOW.
        </div>
      )}
      {BRIEF_FIELDS.map(([k, label, placeholder, rows]) => (
        <div key={k}>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{label}</label>
          <textarea
            disabled={readOnly}
            value={form[k]}
            onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
            placeholder={placeholder}
            rows={rows}
            data-testid={`brief-${k}`}
            className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-sm leading-relaxed focus:outline-none focus:border-text-primary disabled:opacity-60"
          />
        </div>
      ))}
      {!readOnly && (
        <div className="flex gap-3 pt-2">
          <button onClick={() => save(false)} disabled={busy} data-testid="brief-save" className="px-4 py-2.5 text-sm border border-border rounded-lg text-text-primary hover:border-text-primary disabled:opacity-50">
            Save draft
          </button>
          <button onClick={() => save(true)} disabled={busy} data-testid="brief-submit" className="px-4 py-2.5 text-sm font-semibold rounded-lg text-white disabled:opacity-50 inline-flex items-center gap-2" style={{ backgroundColor: accent }}>
            <Check size={13} weight="bold" /> Submit brief
          </button>
        </div>
      )}
    </div>
  );
};

/* ---------- SOW ---------- */
const SOWTab = ({ data, accent, refresh }) => {
  const { format } = useCurrency();
  const { sow, side, engagement } = { ...data, side: data.side };
  const isAgency = side === 'agency';
  const accepted = sow?.status === 'accepted';

  const [items, setItems]       = useState(sow?.items?.length ? sow.items : [{ title: '', description: '', quantity: 1, unit_price: 0, platform: '' }]);
  const [payments, setPayments] = useState(sow?.payment_schedule?.length ? sow.payment_schedule : [{ label: 'Kickoff', amount: 0, due_offset_days: 0 }]);
  const [currency, setCurrency] = useState(sow?.currency || 'INR');
  const [duration, setDuration] = useState(sow?.duration_weeks || '');
  const [notes, setNotes]       = useState(sow?.notes || '');
  const [busy, setBusy]         = useState(false);

  const total = useMemo(() => items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0), [items]);
  const paySum = useMemo(() => payments.reduce((s, p) => s + Number(p.amount || 0), 0), [payments]);

  const draftDisabled = !isAgency || accepted;

  const draft = async () => {
    if (!isAgency) return;
    setBusy(true);
    try {
      await api.post(`/engagements/${engagement.id}/sow`, {
        items: items.map((i) => ({ ...i, quantity: Number(i.quantity || 0), unit_price: Number(i.unit_price || 0) })),
        payment_schedule: payments.map((p) => ({ ...p, amount: Number(p.amount || 0), due_offset_days: Number(p.due_offset_days || 0) })),
        currency, duration_weeks: duration ? Number(duration) : null, notes,
      });
      toast.success('SOW sent to founder for approval');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Draft failed'); }
    finally { setBusy(false); }
  };

  const accept = async () => {
    if (isAgency) return;
    if (!window.confirm(`Accept this Statement of Work? Total ${format(total, currency)}. This will lock the line items and create the milestones + payment schedule.`)) return;
    setBusy(true);
    try {
      await api.post(`/engagements/${engagement.id}/sow/accept`, {});
      toast.success('SOW accepted — engagement is now active!');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Accept failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="engagement-sow">
      {!sow && !isAgency && (
        <div className="border border-dashed border-border rounded-lg p-8 text-center">
          <FileText size={22} className="mx-auto mb-2 text-text-muted/60" />
          <p className="text-sm text-text-muted">Waiting on the agency to draft your SOW.</p>
        </div>
      )}
      {accepted && (
        <div className="bg-success/10 border border-success/30 rounded-lg p-4 inline-flex items-center gap-2 text-sm text-success">
          <Check size={14} weight="bold" /> SOW accepted — engagement is live.
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-heading font-bold text-text-primary">Line items</h3>
          {isAgency && !accepted && (
            <button onClick={() => setItems((p) => [...p, { title: '', description: '', quantity: 1, unit_price: 0, platform: '' }])} data-testid="sow-add-item" className="text-xs text-text-secondary hover:text-text-primary inline-flex items-center gap-1">
              <Plus size={11} weight="bold" /> Add line
            </button>
          )}
        </div>
        <div className="space-y-2">
          {items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_90px_120px_140px_24px] gap-2 bg-surface border border-border rounded-lg p-3" data-testid={`sow-item-${idx}`}>
              <input disabled={draftDisabled} placeholder="Title (e.g. LinkedIn ghostwriting)" value={it.title} onChange={(e) => setItems((p) => p.map((x, i) => i === idx ? { ...x, title: e.target.value } : x))} className="bg-background border border-border rounded px-2 py-1.5 text-sm focus:outline-none focus:border-text-primary disabled:opacity-60" />
              <input disabled={draftDisabled} type="number" placeholder="Qty" value={it.quantity} onChange={(e) => setItems((p) => p.map((x, i) => i === idx ? { ...x, quantity: e.target.value } : x))} className="bg-background border border-border rounded px-2 py-1.5 text-sm focus:outline-none focus:border-text-primary text-right disabled:opacity-60" />
              <input disabled={draftDisabled} type="number" placeholder="Unit price" value={it.unit_price} onChange={(e) => setItems((p) => p.map((x, i) => i === idx ? { ...x, unit_price: e.target.value } : x))} className="bg-background border border-border rounded px-2 py-1.5 text-sm focus:outline-none focus:border-text-primary text-right font-mono disabled:opacity-60" />
              <select disabled={draftDisabled} value={it.platform || ''} onChange={(e) => setItems((p) => p.map((x, i) => i === idx ? { ...x, platform: e.target.value } : x))} className="bg-background border border-border rounded px-2 py-1.5 text-sm focus:outline-none focus:border-text-primary disabled:opacity-60">
                <option value="">No platform</option>
                {PLATFORMS.map((pl) => <option key={pl} value={pl}>{pl.replace(/_/g, ' ')}</option>)}
              </select>
              {isAgency && !accepted ? (
                <button onClick={() => setItems((p) => p.filter((_, i) => i !== idx))} className="text-text-muted hover:text-danger" title="Remove"><Trash size={13} weight="bold" /></button>
              ) : <span />}
            </div>
          ))}
        </div>
        <div className="text-right mt-2 text-sm font-mono text-text-primary">
          <span className="text-text-muted">Total:</span> <strong>{format(total, currency)}</strong>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-heading font-bold text-text-primary">Payment schedule</h3>
          {isAgency && !accepted && (
            <button onClick={() => setPayments((p) => [...p, { label: '', amount: 0, due_offset_days: 0 }])} data-testid="sow-add-payment" className="text-xs text-text-secondary hover:text-text-primary inline-flex items-center gap-1">
              <Plus size={11} weight="bold" /> Add payment
            </button>
          )}
        </div>
        <div className="space-y-2">
          {payments.map((p, idx) => (
            <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_140px_120px_24px] gap-2 bg-surface border border-border rounded-lg p-3" data-testid={`sow-payment-${idx}`}>
              <input disabled={draftDisabled} placeholder="Label (Kickoff, Milestone 1…)" value={p.label} onChange={(e) => setPayments((q) => q.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))} className="bg-background border border-border rounded px-2 py-1.5 text-sm focus:outline-none focus:border-text-primary disabled:opacity-60" />
              <input disabled={draftDisabled} type="number" placeholder="Amount" value={p.amount} onChange={(e) => setPayments((q) => q.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))} className="bg-background border border-border rounded px-2 py-1.5 text-sm focus:outline-none focus:border-text-primary text-right font-mono disabled:opacity-60" />
              <input disabled={draftDisabled} type="number" placeholder="Days after sign-off" value={p.due_offset_days} onChange={(e) => setPayments((q) => q.map((x, i) => i === idx ? { ...x, due_offset_days: e.target.value } : x))} className="bg-background border border-border rounded px-2 py-1.5 text-sm focus:outline-none focus:border-text-primary text-right disabled:opacity-60" />
              {isAgency && !accepted ? (
                <button onClick={() => setPayments((q) => q.filter((_, i) => i !== idx))} className="text-text-muted hover:text-danger"><Trash size={13} weight="bold" /></button>
              ) : <span />}
            </div>
          ))}
        </div>
        {Math.abs(paySum - total) > 0.5 && (
          <div className="text-right mt-2 text-[11px] text-warning inline-flex items-center gap-1 justify-end w-full">
            <Warning size={12} weight="bold" /> Payment schedule {format(paySum, currency)} ≠ line-item total {format(total, currency)}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Currency</label>
          <select disabled={draftDisabled} value={currency} onChange={(e) => setCurrency(e.target.value)} className="bg-surface border border-border rounded-lg px-3 py-2.5 text-sm w-full disabled:opacity-60">
            <option value="INR">INR (₹)</option>
            <option value="USD">USD ($)</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Duration (weeks)</label>
          <input disabled={draftDisabled} type="number" value={duration} onChange={(e) => setDuration(e.target.value)} className="bg-surface border border-border rounded-lg px-3 py-2.5 text-sm w-full disabled:opacity-60" />
        </div>
      </div>

      <div>
        <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Scope notes</label>
        <textarea disabled={draftDisabled} value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="What's NOT included, revision policy, kill-switch clauses, etc." className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-text-primary disabled:opacity-60" />
      </div>

      <div className="flex gap-3 pt-2">
        {isAgency && !accepted && (
          <button onClick={draft} disabled={busy} data-testid="sow-draft" className="px-4 py-2.5 text-sm font-semibold rounded-lg text-white disabled:opacity-50 inline-flex items-center gap-2" style={{ backgroundColor: accent }}>
            <PaperPlaneTilt size={13} weight="bold" /> {sow ? 'Update & resend SOW' : 'Send SOW to founder'}
          </button>
        )}
        {!isAgency && sow && !accepted && (
          <button onClick={accept} disabled={busy} data-testid="sow-accept" className="px-4 py-2.5 text-sm font-semibold rounded-lg text-white disabled:opacity-50 inline-flex items-center gap-2" style={{ backgroundColor: accent }}>
            <Check size={13} weight="bold" /> Accept SOW & start engagement
          </button>
        )}
      </div>
    </div>
  );
};

/* ---------- PIPELINE (Module 05) ---------- */
const PipelineTab = ({ engagementId, side, accent, milestones, engagementStatus }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [openReq, setOpenReq] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/engagements/${engagementId}/requests`);
      setRequests(r.data.requests || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [engagementId]);

  const move = async (req, status, extra = {}) => {
    try {
      const r = await api.patch(`/engagements/${engagementId}/requests/${req.id}`, { status, ...extra });
      setRequests((p) => p.map((x) => x.id === req.id ? r.data.request : x));
      if (openReq && openReq.id === req.id) setOpenReq(r.data.request);
    } catch (e) { toast.error(e.response?.data?.detail || 'Move failed'); }
  };

  const grouped = useMemo(() => {
    const g = {}; REQUEST_STATUSES.forEach((s) => { g[s.id] = []; });
    (requests || []).forEach((r) => { (g[r.status] || g.new).push(r); });
    return g;
  }, [requests]);

  if (engagementStatus !== 'active') {
    return <div className="border border-dashed border-border rounded-lg p-10 text-center text-sm text-text-muted">Pipeline opens after the SOW is accepted.</div>;
  }
  if (loading) return <div className="text-sm text-text-muted">Loading…</div>;

  return (
    <div data-testid="engagement-pipeline">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-heading font-bold text-text-primary">Request pipeline</h3>
        <button onClick={() => setCreateOpen(true)} data-testid="pipeline-new-request" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg text-white hover:opacity-90" style={{ backgroundColor: accent }}>
          <Plus size={11} weight="bold" /> New request
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="pipeline-kanban">
        {REQUEST_STATUSES.map((s) => (
          <div key={s.id} className="bg-surface border border-border rounded-xl p-3 min-h-[200px]">
            <div className="flex items-center justify-between mb-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-text-primary">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} /> {s.label}
              </span>
              <span className="text-[10px] font-mono text-text-muted">{grouped[s.id].length}</span>
            </div>
            <div className="space-y-2">
              {grouped[s.id].map((r) => (
                <button key={r.id} onClick={() => setOpenReq(r)} data-testid={`pipeline-card-${r.id}`} className="text-left w-full bg-background border border-border rounded-lg p-3 hover:border-text-primary transition-colors">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="text-sm font-medium text-text-primary line-clamp-2">{r.title}</div>
                    {r.priority && r.priority !== 'normal' && (
                      <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: r.priority === 'urgent' ? '#ef444422' : '#f59e0b22', color: r.priority === 'urgent' ? '#ef4444' : '#f59e0b' }}>{r.priority}</span>
                    )}
                  </div>
                  {r.platform && <div className="text-[10px] text-text-muted capitalize">{r.platform.replace(/_/g, ' ')}</div>}
                  {r.due_date && <div className="text-[10px] text-text-muted inline-flex items-center gap-1 mt-1"><Clock size={9} weight="bold" /> due {fmtDate(r.due_date)}</div>}
                </button>
              ))}
              {grouped[s.id].length === 0 && <div className="text-[11px] text-text-muted/60 italic py-2 text-center">empty</div>}
            </div>
          </div>
        ))}
      </div>

      {createOpen && <CreateRequestModal accent={accent} engagementId={engagementId} milestones={milestones} side={side} onClose={() => setCreateOpen(false)} onCreated={(r) => { setRequests((p) => [r, ...p]); setCreateOpen(false); }} />}
      {openReq && <RequestDetailModal accent={accent} engagementId={engagementId} side={side} request={openReq} onClose={() => setOpenReq(null)} onMove={move} />}
    </div>
  );
};

const CreateRequestModal = ({ accent, engagementId, milestones, side, onClose, onCreated }) => {
  const [form, setForm] = useState({ title: '', description: '', priority: 'normal', due_date: '', platform: '', milestone_id: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setBusy(true);
    try {
      const body = { ...form, due_date: form.due_date || null, platform: form.platform || null, milestone_id: form.milestone_id || null };
      const r = await api.post(`/engagements/${engagementId}/requests`, body);
      toast.success('Request created');
      onCreated(r.data.request);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Modal accent={accent} onClose={onClose} title="New request" testid="pipeline-create-modal">
      <div className="space-y-3">
        <Input label="Title *" value={form.title} onChange={(v) => setForm((p) => ({ ...p, title: v }))} testid="pipeline-create-title" />
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Description</label>
          <textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={4} data-testid="pipeline-create-description" className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Priority" value={form.priority} onChange={(v) => setForm((p) => ({ ...p, priority: v }))} options={['low','normal','high','urgent']} testid="pipeline-create-priority" />
          <Input label="Due date" type="date" value={form.due_date} onChange={(v) => setForm((p) => ({ ...p, due_date: v }))} testid="pipeline-create-due" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Platform" value={form.platform} onChange={(v) => setForm((p) => ({ ...p, platform: v }))} options={['', ...PLATFORMS]} testid="pipeline-create-platform" />
          <Select label="Milestone" value={form.milestone_id} onChange={(v) => setForm((p) => ({ ...p, milestone_id: v }))} options={[{ v: '', l: 'None' }, ...(milestones || []).map((m) => ({ v: m.id, l: m.title }))]} testid="pipeline-create-milestone" />
        </div>
      </div>
      <div className="flex gap-3 mt-5">
        <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-border rounded-lg text-text-secondary hover:text-text-primary">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="pipeline-create-submit" className="flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50 inline-flex items-center justify-center gap-2" style={{ backgroundColor: accent }}>
          <Check size={13} weight="bold" /> Create request
        </button>
      </div>
    </Modal>
  );
};

const RequestDetailModal = ({ accent, engagementId, side, request, onClose, onMove }) => {
  const [revisions, setRevisions] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [delivery, setDelivery] = useState({ delivery_url: request.delivery_url || '', scheduled_date: request.scheduled_date || '' });

  useEffect(() => {
    api.get(`/engagements/${engagementId}/requests/${request.id}/revisions`).then((r) => setRevisions(r.data.revisions || []));
    // eslint-disable-next-line
  }, [request.id]);

  const sendRevision = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/engagements/${engagementId}/requests/${request.id}/revisions`, { notes: note });
      setRevisions((p) => [...p, r.data.revision]); setNote('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const submitDelivery = async () => {
    setBusy(true);
    try {
      await onMove(request, 'review', { delivery_url: delivery.delivery_url, scheduled_date: delivery.scheduled_date || null });
      toast.success('Sent for review');
    } finally { setBusy(false); }
  };

  return (
    <Modal accent={accent} onClose={onClose} title={request.title} testid="pipeline-detail-modal" wide>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-5">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1">Description</div>
          <p className="text-sm text-text-secondary whitespace-pre-wrap mb-4 leading-relaxed">{request.description || '—'}</p>

          <div className="flex flex-wrap gap-3 text-[11px] text-text-muted mb-4">
            {request.platform && <span>Platform: <span className="text-text-primary capitalize">{request.platform.replace(/_/g, ' ')}</span></span>}
            {request.due_date && <span>Due: <span className="text-text-primary">{fmtDate(request.due_date)}</span></span>}
            {request.scheduled_date && <span>Scheduled: <span className="text-text-primary">{fmtDate(request.scheduled_date)}</span></span>}
            <span>Priority: <span className="text-text-primary capitalize">{request.priority}</span></span>
          </div>

          {request.delivery_url && (
            <div className="bg-surface border border-border rounded-lg p-3 mb-4">
              <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-1">Latest delivery</div>
              <a href={request.delivery_url} target="_blank" rel="noreferrer" className="text-sm font-mono break-all" style={{ color: accent }}>{request.delivery_url}</a>
            </div>
          )}

          {side === 'agency' && (request.status === 'in_progress' || request.status === 'revisions' || request.status === 'new') && (
            <div className="bg-surface border border-border rounded-lg p-3 mb-4 space-y-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Submit delivery</div>
              <input value={delivery.delivery_url} onChange={(e) => setDelivery((p) => ({ ...p, delivery_url: e.target.value }))} placeholder="Delivery URL (Drive, Figma, etc.)" data-testid="pipeline-detail-delivery-url" className="w-full bg-background border border-border rounded px-2 py-1.5 text-sm" />
              <input type="date" value={delivery.scheduled_date || ''} onChange={(e) => setDelivery((p) => ({ ...p, scheduled_date: e.target.value }))} data-testid="pipeline-detail-scheduled" className="w-full bg-background border border-border rounded px-2 py-1.5 text-sm" />
              <button onClick={submitDelivery} disabled={busy} className="text-xs px-3 py-1.5 text-white rounded font-semibold inline-flex items-center gap-1" style={{ backgroundColor: accent }} data-testid="pipeline-detail-submit-delivery">
                <PaperPlaneTilt size={11} weight="bold" /> Send for review
              </button>
            </div>
          )}

          <div className="text-[11px] uppercase tracking-wider font-bold text-text-muted mb-2 inline-flex items-center gap-1.5"><ChatCircle size={11} weight="bold" /> Revisions thread</div>
          <div className="space-y-2 max-h-[260px] overflow-y-auto pr-2" data-testid="pipeline-revisions">
            {revisions.length === 0 && <p className="text-xs text-text-muted/70 italic">No revisions yet.</p>}
            {revisions.map((rv) => (
              <div key={rv.id} className="bg-surface border border-border rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: rv.by_side === 'agency' ? '#6366f1' : '#10b981' }}>{rv.by_side} · {rv.by_name}</div>
                <p className="text-sm text-text-secondary whitespace-pre-wrap">{rv.notes}</p>
                <div className="text-[10px] text-text-muted mt-1">{fmtDate(rv.created_at)}</div>
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Leave a note…" data-testid="pipeline-revision-input" className="flex-1 bg-background border border-border rounded px-2 py-1.5 text-sm focus:outline-none focus:border-text-primary" />
            <button onClick={sendRevision} disabled={busy || !note.trim()} data-testid="pipeline-revision-send" className="text-xs px-3 py-1.5 font-semibold rounded text-white disabled:opacity-50" style={{ backgroundColor: accent }}>Send</button>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-xl p-4 self-start space-y-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Actions</div>
          {side === 'agency' && (
            <>
              {['new', 'in_progress', 'review', 'revisions', 'approved', 'cancelled'].map((s) => (
                <button key={s} disabled={request.status === s} onClick={() => onMove(request, s)} data-testid={`pipeline-detail-move-${s}`} className={`w-full text-left text-xs px-3 py-2 rounded border ${request.status === s ? 'border-text-primary text-text-primary' : 'border-border text-text-secondary hover:border-text-primary'}`}>
                  Move to <span className="capitalize">{s.replace(/_/g, ' ')}</span>
                </button>
              ))}
            </>
          )}
          {side === 'founder' && (
            <>
              {request.status === 'review' && (
                <>
                  <button onClick={() => onMove(request, 'approved')} data-testid="pipeline-detail-approve" className="w-full text-left text-xs px-3 py-2 rounded text-white inline-flex items-center justify-between" style={{ backgroundColor: '#10b981' }}>
                    <span>Approve delivery</span><Check size={11} weight="bold" />
                  </button>
                  <button onClick={() => onMove(request, 'revisions')} data-testid="pipeline-detail-request-revision" className="w-full text-left text-xs px-3 py-2 rounded border border-warning text-warning hover:bg-warning/10">
                    Request revisions
                  </button>
                </>
              )}
              {request.status !== 'cancelled' && request.status !== 'approved' && (
                <button onClick={() => onMove(request, 'cancelled')} className="w-full text-left text-xs px-3 py-2 rounded border border-border text-text-muted hover:text-danger hover:border-danger">
                  Cancel request
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

/* ---------- CALENDAR (Module 07) ---------- */
const CalendarTab = ({ engagementId, side, accent }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [openReq, setOpenReq] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/engagements/${engagementId}/requests`);
      setRequests(r.data.requests || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [engagementId]);

  const move = async (req, status) => {
    try {
      const r = await api.patch(`/engagements/${engagementId}/requests/${req.id}`, { status });
      setRequests((p) => p.map((x) => x.id === req.id ? r.data.request : x));
      if (openReq && openReq.id === req.id) setOpenReq(r.data.request);
    } catch (e) { toast.error(e.response?.data?.detail || 'Move failed'); }
  };

  const setScheduledDate = async (req, newDate) => {
    try {
      const r = await api.patch(`/engagements/${engagementId}/requests/${req.id}`, { scheduled_date: newDate });
      setRequests((p) => p.map((x) => x.id === req.id ? r.data.request : x));
      toast.success('Rescheduled');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="text-sm text-text-muted">Loading…</div>;

  // Build month grid (Monday-first)
  const year = cursor.getFullYear(); const month = cursor.getMonth();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7) cells.push(null);

  const byDate = {};
  requests.forEach((r) => {
    const key = (r.scheduled_date || r.due_date || '').slice(0, 10);
    if (!key) return;
    (byDate[key] = byDate[key] || []).push(r);
  });

  const handleDrop = (e, cellDate) => {
    e.preventDefault();
    const reqId = e.dataTransfer.getData('text/req-id');
    const req = requests.find((x) => x.id === reqId);
    if (req && cellDate) setScheduledDate(req, cellDate.toISOString().slice(0, 10));
  };

  return (
    <div data-testid="engagement-calendar">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-heading font-bold text-text-primary">{cursor.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</h3>
        <div className="flex items-center gap-1">
          <button onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="calendar-prev" className="p-1.5 rounded border border-border hover:border-text-primary"><CaretLeft size={12} weight="bold" /></button>
          <button onClick={() => { const t = new Date(); setCursor(new Date(t.getFullYear(), t.getMonth(), 1)); }} data-testid="calendar-today" className="text-xs px-3 py-1.5 rounded border border-border hover:border-text-primary">Today</button>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="calendar-next" className="p-1.5 rounded border border-border hover:border-text-primary"><CaretRight size={12} weight="bold" /></button>
        </div>
      </div>

      {requests.filter((r) => r.scheduled_date || r.due_date).length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-10 text-center text-sm text-text-muted">
          <CalendarBlank size={24} className="mx-auto mb-2 text-text-muted/40" />
          No scheduled deliverables yet. Add a request and set a scheduled date.
        </div>
      ) : (
        <div className="grid grid-cols-7 border border-border rounded-xl overflow-hidden bg-surface" data-testid="calendar-grid">
          {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d) => (
            <div key={d} className="text-[10px] uppercase tracking-wider font-bold text-text-muted p-2 border-b border-border bg-background text-center">{d}</div>
          ))}
          {cells.map((c, i) => {
            const key = c?.toISOString().slice(0, 10);
            const items = byDate[key] || [];
            const isToday = key === todayKey();
            return (
              <div
                key={i}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(e, c)}
                className={`min-h-[110px] border-b border-r border-border p-1.5 ${c ? '' : 'bg-background/50'}`}
              >
                {c && (
                  <div className={`text-[11px] font-mono mb-1 inline-flex items-center justify-center w-5 h-5 rounded-full ${isToday ? 'text-white' : 'text-text-muted'}`} style={isToday ? { backgroundColor: accent } : {}}>
                    {c.getDate()}
                  </div>
                )}
                <div className="space-y-1">
                  {items.slice(0, 3).map((r) => {
                    const statusColor = REQUEST_STATUSES.find((s) => s.id === r.status)?.color || '#64748b';
                    return (
                      <button
                        key={r.id}
                        draggable={side === 'agency'}
                        onDragStart={(e) => { e.dataTransfer.setData('text/req-id', r.id); }}
                        onClick={() => setOpenReq(r)}
                        data-testid={`calendar-item-${r.id}`}
                        className="w-full text-left text-[11px] bg-background border border-border rounded px-1.5 py-1 hover:border-text-primary"
                      >
                        <div className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: statusColor }} />
                          <span className="text-text-primary truncate">{r.title}</span>
                        </div>
                        {r.platform && <div className="text-[9px] text-text-muted capitalize ml-3 truncate">{r.platform.replace(/_/g, ' ')}</div>}
                      </button>
                    );
                  })}
                  {items.length > 3 && <div className="text-[10px] text-text-muted px-1">+{items.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-[10px] text-text-muted mt-2">{side === 'agency' ? 'Drag a deliverable to reschedule. ' : ''}Click any pill to open the request.</p>

      {openReq && <RequestDetailModal accent={accent} engagementId={engagementId} side={side} request={openReq} onClose={() => setOpenReq(null)} onMove={move} />}
    </div>
  );
};

/* ---------- PAYMENTS ---------- */
const PaymentsTab = ({ data, accent, refresh }) => {
  const { format } = useCurrency();
  const { payments, side, engagement } = { ...data, side: data.side };
  const [busy, setBusy] = useState('');

  const markPaid = async (p) => {
    if (side !== 'founder') return;
    if (!window.confirm(`Mark "${p.label}" — ${format(p.amount, p.currency || 'INR')} — as paid? You'll record the manual receipt offline for now.`)) return;
    setBusy(p.id);
    try {
      await api.post(`/engagements/${engagement.id}/payments/${p.id}/mark-paid`, {});
      toast.success('Marked as paid');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(''); }
  };

  if (!payments.length) return <div className="border border-dashed border-border rounded-lg p-10 text-center text-sm text-text-muted">No payment schedule yet — accept the SOW to generate it.</div>;
  const totalPaid = payments.filter((p) => p.status === 'paid').reduce((s, p) => s + (p.amount || 0), 0);
  const totalDue = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const payCur = payments[0]?.currency || 'INR';

  return (
    <div data-testid="engagement-payments">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Total billed</div>
          <div className="text-2xl font-mono font-bold text-text-primary">{format(totalDue, payCur)}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Paid so far</div>
          <div className="text-2xl font-mono font-bold" style={{ color: accent }}>{format(totalPaid, payCur)}</div>
        </div>
      </div>
      <div className="space-y-2">
        {payments.map((p) => (
          <div key={p.id} className="bg-surface border border-border rounded-lg px-4 py-3 flex items-center justify-between gap-4" data-testid={`payment-row-${p.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-text-primary">{p.label}</span>
                <StatusPill label="" value={p.status} testid={`payment-status-${p.id}`} />
              </div>
              <div className="text-[11px] text-text-muted">Due {fmtDate(p.due_date)}{p.marked_paid_at ? ` · Paid ${fmtDate(p.marked_paid_at)}` : ''}</div>
            </div>
            <div className="text-right">
              <div className="font-mono font-semibold text-text-primary">{format(p.amount || 0, p.currency || 'INR')}</div>
              {p.status !== 'paid' && side === 'founder' && (
                <button onClick={() => markPaid(p)} disabled={busy === p.id} data-testid={`payment-mark-paid-${p.id}`} className="mt-1 text-xs px-2.5 py-1 text-white rounded font-semibold disabled:opacity-50" style={{ backgroundColor: accent }}>
                  Mark paid
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-text-muted mt-3">MVP: payments are marked paid manually. Razorpay checkout lands in Sprint 5.</p>
    </div>
  );
};

/* ---------- CONTRACTS / NDA VAULT ---------- */
const ContractsTab = ({ data, accent, refresh }) => {
  const { contracts, engagement, side } = { ...data, side: data.side };
  const [open, setOpen] = useState(false);

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.title}"?`)) return;
    try {
      await api.delete(`/engagements/${engagement.id}/contracts/${c.id}`);
      toast.success('Deleted');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="engagement-contracts">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-heading font-bold text-text-primary">Contracts & NDA vault</h3>
        <button onClick={() => setOpen(true)} data-testid="contracts-add" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg text-white" style={{ backgroundColor: accent }}>
          <UploadSimple size={11} weight="bold" /> Add document
        </button>
      </div>
      {contracts.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-10 text-center text-sm text-text-muted">
          <ShieldCheck size={22} className="mx-auto mb-2 text-text-muted/40" />
          No contracts uploaded yet. Add your NDA, MSA, or any signed SOW.
        </div>
      ) : (
        <div className="space-y-2">
          {contracts.map((c) => (
            <div key={c.id} className="bg-surface border border-border rounded-lg px-4 py-3 flex items-center justify-between gap-4" data-testid={`contract-row-${c.id}`}>
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${accent}1a`, color: accent }}>{c.kind}</span>
                  <span className="text-sm font-medium text-text-primary">{c.title}</span>
                </div>
                <div className="text-[11px] text-text-muted">{c.uploaded_by_name} · {c.uploaded_by_side} · {fmtDate(c.created_at)}</div>
                {c.notes && <div className="text-xs text-text-secondary mt-1">{c.notes}</div>}
              </div>
              <div className="flex items-center gap-2">
                {c.file_id && (
                  <a href={`${API}/files/${c.file_id}`} target="_blank" rel="noreferrer" className="text-xs text-text-secondary hover:text-text-primary inline-flex items-center gap-1">
                    Download <ArrowRight size={10} weight="bold" />
                  </a>
                )}
                {(c.uploaded_by_side === side || side === 'founder') && (
                  <button onClick={() => remove(c)} className="text-text-muted hover:text-danger" data-testid={`contract-remove-${c.id}`}><Trash size={12} weight="bold" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {open && <AddContractModal accent={accent} engagementId={engagement.id} onClose={() => setOpen(false)} onSaved={async () => { setOpen(false); await refresh(); }} />}
    </div>
  );
};

const AddContractModal = ({ accent, engagementId, onClose, onSaved }) => {
  const [form, setForm] = useState({ kind: 'nda', title: '', notes: '', file_id: null });
  const [filename, setFilename] = useState('');
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post(`/files/upload`, fd);
      setForm((p) => ({ ...p, file_id: r.data.file_id }));
      setFilename(file.name);
      toast.success('Uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setBusy(true);
    try {
      await api.post(`/engagements/${engagementId}/contracts`, form);
      toast.success('Added to vault');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Modal accent={accent} onClose={onClose} title="Add document to vault" testid="contracts-add-modal">
      <div className="space-y-3">
        <Select label="Type" value={form.kind} onChange={(v) => setForm((p) => ({ ...p, kind: v }))} options={[{v:'nda',l:'NDA'},{v:'msa',l:'MSA'},{v:'sow',l:'Signed SOW PDF'},{v:'other',l:'Other'}]} testid="contracts-add-kind" />
        <Input label="Title *" value={form.title} onChange={(v) => setForm((p) => ({ ...p, title: v }))} testid="contracts-add-title" />
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Notes</label>
          <textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} rows={2} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" data-testid="contracts-add-notes" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Attachment</label>
          <input type="file" onChange={(e) => upload(e.target.files?.[0])} data-testid="contracts-add-file" className="text-xs" />
          {filename && <div className="text-[11px] text-text-secondary mt-1">{filename}</div>}
        </div>
      </div>
      <div className="flex gap-3 mt-5">
        <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-border rounded-lg text-text-secondary hover:text-text-primary">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="contracts-add-submit" className="flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50 inline-flex items-center justify-center gap-2" style={{ backgroundColor: accent }}>
          <Check size={13} weight="bold" /> Save to vault
        </button>
      </div>
    </Modal>
  );
};

/* ---------- shared form primitives ---------- */
const Input = ({ label, value, onChange, type = 'text', testid }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary" />
  </div>
);

const Select = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{label}</label>
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="appearance-none w-full bg-background border border-border rounded-lg pl-3 pr-7 py-2 text-sm cursor-pointer focus:outline-none focus:border-text-primary">
        {options.map((o) => {
          const v = typeof o === 'object' ? o.v : o;
          const l = typeof o === 'object' ? o.l : (o || '—').replace(/_/g, ' ');
          return <option key={String(v)} value={v}>{l}</option>;
        })}
      </select>
      <CaretDown size={10} weight="bold" className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
    </div>
  </div>
);

/* ---------- BRAND TAB (engagement-scoped, read-only for agency, recommendations channel) ---------- */
const BrandTab = ({ engagementId, side, accent }) => {
  const [data, setData] = useState(null);
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [b, r] = await Promise.all([
          api.get(`/engagements/${engagementId}/brand-brain`),
          api.get(`/engagements/${engagementId}/brand-brain/recommendations`),
        ]);
        if (!cancelled) {
          setData(b.data);
          setRecs(r.data.recommendations || []);
        }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [engagementId, reloadKey]);

  if (loading) return <div className="text-sm text-text-muted">Loading…</div>;
  if (!data?.active) {
    return (
      <div className="border border-dashed border-border rounded-xl p-10 text-center" data-testid="engagement-brand-empty">
        <Brain size={24} className="mx-auto mb-2 text-text-muted/40" />
        <p className="text-sm text-text-secondary mb-1">{side === 'founder' ? 'You haven\u2019t generated your Brand Brain yet.' : 'The founder hasn\u2019t generated their Brand Brain yet.'}</p>
        <p className="text-xs text-text-muted">{side === 'founder' ? <>Head to <a href="/startup/marketing/brand" className="underline" style={{ color: accent }}>Marketing &rarr; Brand Brain</a> to set it up.</> : 'Nudge them — having a brand kit shortens onboarding by days.'}</p>
      </div>
    );
  }
  const kit = data.active.kit || {};
  const grouped = recs.reduce((g, r) => { (g[r.section] = g[r.section] || []).push(r); return g; }, {});

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5" data-testid="engagement-brand">
      <div className="space-y-5">
        {side === 'agency' && (
          <div className="bg-surface border-l-2 rounded-r-lg p-3 text-xs text-text-secondary" style={{ borderLeftColor: accent }}>
            Read-only view of the founder&rsquo;s brand kit. Use it to shape posts &mdash; and drop recommendations any section feels off.
          </div>
        )}
        <BrandSection title="Brand description" section="brand_description" content={kit.brand_description} recs={grouped.brand_description} accent={accent} side={side} engagementId={engagementId} reload={reload} />
        <BrandSection title="Mission" section="mission" content={kit.mission} recs={grouped.mission} accent={accent} side={side} engagementId={engagementId} reload={reload} />
        <BrandSection title="Vision"  section="vision"  content={kit.vision}  recs={grouped.vision}  accent={accent} side={side} engagementId={engagementId} reload={reload} />
        <BrandSection title="Values"  section="values"  content={(kit.values || []).join(' &middot; ')} recs={grouped.values} accent={accent} side={side} engagementId={engagementId} reload={reload} />
        <BrandSection title="Tone of voice" section="tone_axes" recs={grouped.tone_axes} accent={accent} side={side} engagementId={engagementId} reload={reload}
          custom={
            <div className="space-y-2">
              {(kit.tone_axes || []).map((a, i) => (
                <div key={i} className="flex items-center gap-3 text-xs">
                  <span className="text-text-muted w-24 truncate">{a.label}</span>
                  <span className="text-text-muted text-[10px] w-20 text-right truncate">{a.left}</span>
                  <div className="flex-1 h-1 bg-border rounded-full relative">
                    <div className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full" style={{ left: `${a.position}%`, backgroundColor: accent, transform: 'translate(-50%, -50%)' }} />
                  </div>
                  <span className="text-text-muted text-[10px] w-20 truncate">{a.right}</span>
                </div>
              ))}
            </div>
          }
        />
        <BrandSection title="Do say"   section="do_words"   content={(kit.do_words || []).join(', ')}   recs={grouped.do_words}   accent={accent} side={side} engagementId={engagementId} reload={reload} />
        <BrandSection title="Don&rsquo;t say" section="dont_words" content={(kit.dont_words || []).join(', ')} recs={grouped.dont_words} accent={accent} side={side} engagementId={engagementId} reload={reload} />
        <BrandSection title="Sample taglines" section="sample_taglines" recs={grouped.sample_taglines} accent={accent} side={side} engagementId={engagementId} reload={reload}
          custom={<ul className="text-sm text-text-secondary space-y-1 list-disc pl-5">{(kit.sample_taglines || []).map((t, i) => <li key={i}>{t}</li>)}</ul>}
        />
        <BrandSection title="Sample social posts" section="sample_social_posts" recs={grouped.sample_social_posts} accent={accent} side={side} engagementId={engagementId} reload={reload}
          custom={
            <div className="space-y-2">
              {(kit.sample_social_posts || []).map((p, i) => (
                <div key={i} className="bg-background border border-border rounded p-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-1">{p.platform}</div>
                  <p className="text-xs text-text-secondary whitespace-pre-wrap leading-relaxed">{p.copy}</p>
                </div>
              ))}
            </div>
          }
        />
      </div>
      <aside className="lg:sticky lg:top-4 self-start space-y-3 bg-surface border border-border rounded-xl p-4">
        <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Recommendations summary</div>
        <div className="text-2xl font-mono font-bold text-text-primary">{recs.filter((r) => r.status === 'open').length} <span className="text-xs text-text-muted">open</span></div>
        <div className="text-[11px] text-text-secondary">{recs.filter((r) => r.status === 'accepted').length} accepted &middot; {recs.filter((r) => r.status === 'dismissed').length} dismissed</div>
        <div className="text-[10px] text-text-muted leading-relaxed pt-2 border-t border-border">{side === 'agency' ? 'Click "Suggest a change" under any section to file a recommendation.' : 'The agency can flag specific sections that need a refresh \u2014 you stay in control of the kit.'}</div>
      </aside>
    </div>
  );
};

const BrandSection = ({ title, section, content, custom, recs, accent, side, engagementId, reload }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [suggested, setSuggested] = useState('');
  const [busy, setBusy] = useState(false);
  const openRecs = (recs || []).filter((r) => r.status === 'open');
  const submit = async () => {
    if (!note.trim()) { toast.error('Note required'); return; }
    setBusy(true);
    try {
      await api.post(`/engagements/${engagementId}/brand-brain/recommendations`, { section, note, suggested_text: suggested });
      toast.success('Recommendation sent');
      setNote(''); setSuggested(''); setOpen(false); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const resolve = async (rec, status) => {
    try {
      await api.patch(`/engagements/${engagementId}/brand-brain/recommendations/${rec.id}`, { status });
      toast.success(`Marked ${status}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="bg-surface border border-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[10px] uppercase tracking-[0.18em] font-bold text-text-muted" dangerouslySetInnerHTML={{ __html: title }} />
        {openRecs.length > 0 && (
          <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${accent}1a`, color: accent }}>{openRecs.length} open</span>
        )}
      </div>
      {custom ? custom : <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: content || '<em class="text-text-muted/50">empty</em>' }} />}

      {/* Recommendations */}
      {(recs || []).length > 0 && (
        <div className="mt-3 space-y-2 border-t border-border pt-3">
          {recs.map((r) => (
            <div key={r.id} className="bg-background border border-border rounded-lg p-2.5" data-testid={`brand-rec-${r.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent }}>{r.created_by_name}</span>
                <span className="text-[10px] uppercase tracking-wider font-bold text-text-muted">{r.status}</span>
              </div>
              <p className="text-xs text-text-secondary mb-1">{r.note}</p>
              {r.suggested_text && <p className="text-xs text-text-primary italic bg-surface border-l-2 pl-2 py-1" style={{ borderLeftColor: accent }}>Suggested: {r.suggested_text}</p>}
              {side === 'founder' && r.status === 'open' && (
                <div className="flex gap-1 mt-2">
                  <button onClick={() => resolve(r, 'accepted')} data-testid={`brand-rec-accept-${r.id}`} className="text-[10px] px-2 py-1 rounded text-white inline-flex items-center gap-1" style={{ backgroundColor: accent }}><ThumbsUp size={9} weight="bold" /> Accept</button>
                  <button onClick={() => resolve(r, 'dismissed')} data-testid={`brand-rec-dismiss-${r.id}`} className="text-[10px] px-2 py-1 rounded border border-border text-text-muted hover:text-danger inline-flex items-center gap-1"><ThumbsDown size={9} weight="bold" /> Dismiss</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Agency-only — suggest a change */}
      {side === 'agency' && (
        <div className="mt-3 border-t border-border pt-3">
          {!open ? (
            <button onClick={() => setOpen(true)} data-testid={`brand-rec-add-${section}`} className="text-[10px] uppercase tracking-wider font-bold text-text-secondary hover:text-text-primary inline-flex items-center gap-1">
              <Plus size={10} weight="bold" /> Suggest a change
            </button>
          ) : (
            <div className="space-y-2">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="What feels off?" data-testid={`brand-rec-note-${section}`} className="w-full bg-background border border-border rounded px-2 py-1.5 text-sm" />
              <textarea value={suggested} onChange={(e) => setSuggested(e.target.value)} rows={2} placeholder="Optional: suggested replacement copy" data-testid={`brand-rec-suggested-${section}`} className="w-full bg-background border border-border rounded px-2 py-1.5 text-sm" />
              <div className="flex gap-2">
                <button onClick={submit} disabled={busy} data-testid={`brand-rec-send-${section}`} className="text-xs px-3 py-1.5 rounded text-white font-semibold" style={{ backgroundColor: accent }}>Send</button>
                <button onClick={() => { setOpen(false); setNote(''); setSuggested(''); }} className="text-xs px-3 py-1.5 rounded border border-border">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ---------- METRICS TAB (shared, both sides can log snapshots) ---------- */
const MetricsTab = ({ engagementId, side, accent }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [addChan, setAddChan] = useState(false);
  const [snapFor, setSnapFor] = useState(null);
  const reload = () => setReloadKey((k) => k + 1);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/engagements/${engagementId}/metrics`);
        if (!cancelled) setData(r.data);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [engagementId, reloadKey]);

  if (loading) return <div className="text-sm text-text-muted">Loading…</div>;
  if (!data) return null;
  const vs = data.visibility_score;
  const bandColor = { high: '#10b981', medium: '#f59e0b', low: '#ef4444', minimal: '#64748b' }[vs.band] || '#64748b';

  return (
    <div data-testid="engagement-metrics">
      <div className="bg-surface border border-border rounded-2xl p-5 mb-5 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-5 items-center">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-1">Founder Visibility Score</div>
          <div className="flex items-baseline gap-2">
            <span className="text-5xl font-mono font-bold" style={{ color: bandColor }} data-testid="engagement-metrics-score">{vs.score}</span>
            <span className="text-lg font-mono text-text-muted">/100</span>
          </div>
          <div className="text-xs uppercase tracking-wider font-bold mt-1" style={{ color: bandColor }}>{vs.band}</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(vs.components).map(([k, v]) => (
            <div key={k} className="bg-background border border-border rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted truncate">{k.replace(/_/g, ' ')}</div>
              <div className="text-xl font-mono font-bold text-text-primary mt-0.5">{v}<span className="text-xs text-text-muted">/25</span></div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Channels ({data.channels.length})</div>
        <button onClick={() => setAddChan(true)} data-testid="engagement-metrics-add-channel" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white" style={{ backgroundColor: accent }}>
          <Plus size={11} weight="bold" /> Add channel
        </button>
      </div>

      {data.channels.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-10 text-center text-sm text-text-muted">
          No channels tracked yet. {side === 'agency' ? 'Add the founder\u2019s social handles to start tracking.' : 'Add your channels to start tracking.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.channels.map((ch) => {
            const snaps = data.snapshots_by_channel[ch.id] || [];
            const latest = snaps[0]; const prev = snaps[1];
            const delta = latest && prev ? latest.followers - prev.followers : 0;
            return (
              <div key={ch.id} className="bg-surface border border-border rounded-xl p-4" data-testid={`engagement-metrics-channel-${ch.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-sm font-semibold text-text-primary capitalize">{ch.platform}</div>
                    <div className="text-[11px] text-text-muted">@{ch.handle}</div>
                  </div>
                  <button onClick={() => setSnapFor(ch)} data-testid={`engagement-metrics-log-${ch.id}`} className="text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1.5 rounded border border-border hover:border-text-primary">
                    <Plus size={10} weight="bold" /> Log
                  </button>
                </div>
                {latest ? (
                  <div className="flex items-end gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Followers</div>
                      <div className="text-xl font-mono font-bold text-text-primary">{(latest.followers || 0).toLocaleString('en-IN')}</div>
                    </div>
                    {prev && (
                      <div className={`text-[11px] font-mono font-semibold pb-1 ${delta >= 0 ? 'text-success' : 'text-danger'}`}>
                        {delta >= 0 ? '+' : ''}{delta.toLocaleString('en-IN')}
                      </div>
                    )}
                    <div className="ml-auto text-right">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Eng %</div>
                      <div className="text-sm font-mono font-semibold text-text-primary">{(latest.engagement_rate || 0).toFixed(2)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Posts/30d</div>
                      <div className="text-sm font-mono font-semibold text-text-primary">{latest.posts_last_30 || 0}</div>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-text-muted/70">No snapshots yet.</p>
                )}
                {snaps.length > 0 && <div className="text-[10px] text-text-muted mt-2">{snaps.length} snapshot{snaps.length === 1 ? '' : 's'}</div>}
              </div>
            );
          })}
        </div>
      )}

      {addChan && <EngagementChannelModal accent={accent} engagementId={engagementId} supported={data.supported_platforms} onClose={() => setAddChan(false)} onSaved={() => { setAddChan(false); reload(); }} />}
      {snapFor && <EngagementSnapshotModal accent={accent} engagementId={engagementId} channel={snapFor} onClose={() => setSnapFor(null)} onSaved={() => { setSnapFor(null); reload(); }} />}
    </div>
  );
};

const EngagementChannelModal = ({ accent, engagementId, supported, onClose, onSaved }) => {
  const [form, setForm] = useState({ platform: supported[0] || 'linkedin', handle: '', display_name: '', url: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.handle.trim()) { toast.error('Handle required'); return; }
    setBusy(true);
    try {
      await api.post(`/engagements/${engagementId}/metrics/channels`, form);
      toast.success('Channel added');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Modal accent={accent} onClose={onClose} title="Add channel" testid="engagement-metrics-channel-modal">
      <div className="space-y-3">
        <Select label="Platform" value={form.platform} onChange={(v) => setForm((p) => ({ ...p, platform: v }))} options={supported} testid="engagement-metrics-platform" />
        <Input label="Handle *" value={form.handle} onChange={(v) => setForm((p) => ({ ...p, handle: v }))} testid="engagement-metrics-handle" />
        <Input label="URL"      value={form.url}    onChange={(v) => setForm((p) => ({ ...p, url: v }))} testid="engagement-metrics-url" />
      </div>
      <div className="flex gap-3 mt-5">
        <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-border rounded-lg">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="engagement-metrics-channel-submit" className="flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: accent }}>{busy ? 'Saving\u2026' : 'Save'}</button>
      </div>
    </Modal>
  );
};

const EngagementSnapshotModal = ({ accent, engagementId, channel, onClose, onSaved }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ followers: 0, engagement_rate: 0, posts_last_30: 0, captured_at: today });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/engagements/${engagementId}/metrics/snapshots`, {
        channel_id: channel.id,
        followers: Number(form.followers || 0),
        engagement_rate: Number(form.engagement_rate || 0),
        posts_last_30: Number(form.posts_last_30 || 0),
        captured_at: new Date(form.captured_at).toISOString(),
      });
      toast.success('Snapshot logged');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Modal accent={accent} onClose={onClose} title={`Log snapshot \u00b7 ${channel.platform} @${channel.handle}`} testid="engagement-metrics-snapshot-modal">
      <div className="space-y-3">
        <Input label="Followers *"            type="number" value={form.followers} onChange={(v) => setForm((p) => ({ ...p, followers: v }))} testid="engagement-snap-followers" />
        <Input label="Engagement rate (%)"     type="number" value={form.engagement_rate} onChange={(v) => setForm((p) => ({ ...p, engagement_rate: v }))} testid="engagement-snap-engagement" />
        <Input label="Posts in last 30 days"   type="number" value={form.posts_last_30} onChange={(v) => setForm((p) => ({ ...p, posts_last_30: v }))} testid="engagement-snap-posts" />
        <Input label="Snapshot date"           type="date"   value={form.captured_at} onChange={(v) => setForm((p) => ({ ...p, captured_at: v }))} testid="engagement-snap-date" />
      </div>
      <div className="flex gap-3 mt-5">
        <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-border rounded-lg">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="engagement-snap-submit" className="flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: accent }}>{busy ? 'Saving\u2026' : 'Save'}</button>
      </div>
    </Modal>
  );
};



const Modal = ({ accent, onClose, title, testid, wide, children }) => (
  <div className="fixed inset-0 bg-black/60 backdrop-blur z-50 flex items-start justify-center pt-12 p-4 overflow-y-auto" onClick={onClose} data-testid={testid}>
    <div className={`bg-surface border border-border rounded-xl p-6 w-full ${wide ? 'max-w-3xl' : 'max-w-md'} my-4`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start justify-between mb-4 gap-3">
        <h3 className="font-heading font-bold text-text-primary text-lg" style={{ letterSpacing: '-0.01em' }}>{title}</h3>
        <button onClick={onClose} className="text-text-muted hover:text-text-primary" data-testid={`${testid}-close`}><X size={18} weight="bold" /></button>
      </div>
      {children}
    </div>
  </div>
);

export default EngagementWorkspace;
